import React, { useState, useEffect } from 'react';
import './TimeFrameModal.css';

const TimeFrameModal = ({
    isOpen,
    onClose,
    onSave,
    selectedDay,
    existingRanges = [],
    allSelectedDays = [],
    globalStartTime,
    globalEndTime,
    editMode = false
}) => {
    const [timeRanges, setTimeRanges] = useState([{ start_time: '', end_time: '' }]);
    const [applyToDays, setApplyToDays] = useState([selectedDay]);
    const [errors, setErrors] = useState([]);

    useEffect(() => {
        if (isOpen) {
            if (editMode && existingRanges.length > 0) {
                setTimeRanges(existingRanges.map(r => ({ ...r })));
            } else {
                setTimeRanges([{ start_time: '', end_time: '' }]);
            }
            setApplyToDays([selectedDay]);
            setErrors([]);
        }
    }, [isOpen, selectedDay, editMode, existingRanges]);

    const formatTime = (time) => {
        if (!time) return '';
        // Return time in 24-hour format (HH:MM)
        return time;
    };

    const timeToMinutes = (time) => {
        if (!time) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const addTimeRange = () => {
        setTimeRanges([...timeRanges, { start_time: '', end_time: '' }]);
    };

    const removeTimeRange = (index) => {
        // Always allow deletion, keep at least one empty range
        const updated = timeRanges.filter((_, i) => i !== index);
        if (updated.length === 0) {
            setTimeRanges([{ start_time: '', end_time: '' }]);
        } else {
            setTimeRanges(updated);
        }
        // Clear error for this index
        setErrors(errors.filter(e => e.index !== index));
    };

    const updateTimeRange = (index, field, value) => {
        const updated = [...timeRanges];
        updated[index] = { ...updated[index], [field]: value };
        setTimeRanges(updated);

        // Clear errors for this range when user makes changes
        setErrors(errors.filter(e => e.index !== index));
    };

    const toggleApplyToDay = (day) => {
        if (applyToDays.includes(day)) {
            setApplyToDays(applyToDays.filter(d => d !== day));
        } else {
            setApplyToDays([...applyToDays, day]);
        }
    };

    const selectAllDays = () => {
        setApplyToDays([...allSelectedDays]);
    };

    const deselectAllDays = () => {
        setApplyToDays([selectedDay]);
    };

    const validateRanges = () => {
        const newErrors = [];

        timeRanges.forEach((range, index) => {
            let hasError = false;

            // Check if both times are filled
            if (!range.start_time && !range.end_time) {
                newErrors.push({ index, message: 'Both start and end times are required' });
                hasError = true;
            } else if (!range.start_time) {
                newErrors.push({ index, message: 'Start time is required' });
                hasError = true;
            } else if (!range.end_time) {
                newErrors.push({ index, message: 'End time is required' });
                hasError = true;
            }

            // Skip further validation if times are missing
            if (hasError) return;

            const start = timeToMinutes(range.start_time);
            const end = timeToMinutes(range.end_time);

            // Check if start < end
            if (start >= end) {
                newErrors.push({ index, message: 'Start time must be before end time' });
                return;
            }

            // Check if within global range
            if (globalStartTime && globalEndTime) {
                const globalStart = timeToMinutes(globalStartTime);
                const globalEnd = timeToMinutes(globalEndTime);

                if (start < globalStart || end > globalEnd) {
                    newErrors.push({
                        index,
                        message: `Time range must be within global campaign time (${formatTime(globalStartTime)} - ${formatTime(globalEndTime)})`
                    });
                    return;
                }
            }

            // Check for overlaps with other ranges in the form
            for (let i = 0; i < timeRanges.length; i++) {
                if (i === index) continue;

                const otherRange = timeRanges[i];
                if (!otherRange.start_time || !otherRange.end_time) continue;

                const otherStart = timeToMinutes(otherRange.start_time);
                const otherEnd = timeToMinutes(otherRange.end_time);

                // Check overlap: ranges overlap if one starts before the other ends
                const overlaps = (start < otherEnd && end > otherStart);

                if (overlaps) {
                    newErrors.push({
                        index,
                        message: `Time range overlaps with range #${i + 1} (${formatTime(otherRange.start_time)} - ${formatTime(otherRange.end_time)})`
                    });
                    break;
                }
            }
        });

        setErrors(newErrors);
        return newErrors.length === 0;
    };

    const handleSave = () => {
        if (!validateRanges()) {
            return;
        }

        if (applyToDays.length === 0) {
            alert('Please select at least one day to apply these time frames');
            return;
        }

        onSave(timeRanges, applyToDays);
        onClose();
    };

    const getErrorForRange = (index) => {
        return errors.find(e => e.index === index);
    };

    if (!isOpen) return null;

    return (
        <div className="timeframe-modal-overlay" onClick={onClose}>
            <div className="timeframe-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="close linear-gradient"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={onClose}
                >
                    X
                </button>
                <div className="timeframe-modal-header">
                    <h5 className="modal-title">{editMode ? 'Edit Time Frames' : 'Add Time Frames'}</h5>
                </div>

                <div className="timeframe-modal-body">
                    {/* Time Ranges List */}
                    <div className="modal-section">
                        <div className="section-header">
                            <label className="section-label">Time Ranges</label>
                            <button
                                type="button"
                                className="btn-add-range"
                                onClick={addTimeRange}
                            >
                                <i className="fa fa-plus"></i>
                                Add Another Range
                            </button>
                        </div>

                        <div className="time-ranges-table">
                            {/* Table Header - Appears only once */}
                            <div className="time-ranges-table-header">
                                <div className="table-col-number">#</div>
                                <div className="table-col-start">Start Time<span style={{ color: 'red' }}>*</span></div>
                                <div className="table-col-end">End Time<span style={{ color: 'red' }}>*</span></div>
                                <div className="table-col-action">Action</div>
                            </div>

                            {/* Table Body - Rows added dynamically */}
                            <div className="time-ranges-table-body">
                                {timeRanges.map((range, index) => {
                                    const error = getErrorForRange(index);
                                    return (
                                        <div key={index} className="time-range-table-row-wrapper">
                                            <div className="time-range-table-row">
                                                <div className="table-col-number">
                                                    <span className="range-number-badge">{index + 1}</span>
                                                </div>

                                                <div className="table-col-start">
                                                    <input
                                                        type="time"
                                                        className={`form-control ${error ? 'is-invalid' : ''}`}
                                                        value={range.start_time}
                                                        onChange={(e) => updateTimeRange(index, 'start_time', e.target.value)}
                                                        style={{
                                                            border: error ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                                            borderRadius: '5px',
                                                            padding: '10px 15px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                </div>

                                                <div className="table-col-end">
                                                    <input
                                                        type="time"
                                                        className={`form-control ${error ? 'is-invalid' : ''}`}
                                                        value={range.end_time}
                                                        onChange={(e) => updateTimeRange(index, 'end_time', e.target.value)}
                                                        style={{
                                                            border: error ? '1px solid #dc3545' : '1px solid #e1e5e9',
                                                            borderRadius: '5px',
                                                            padding: '10px 15px',
                                                            fontSize: '14px'
                                                        }}
                                                    />
                                                </div>

                                                <div className="table-col-action">
                                                    <button
                                                        type="button"
                                                        className="btn-delete-range"
                                                        onClick={() => removeTimeRange(index)}
                                                        title="Delete this time range"
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            {error && (
                                                <div className="range-error-row">
                                                    <i className="fa fa-exclamation-circle"></i>
                                                    {error.message}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Apply to Days Section */}
                    <div className="modal-section">
                        <div className="section-header">
                            <label className="section-label">Apply to Days</label>
                            <div className="day-select-actions">
                                <button
                                    type="button"
                                    className="btn-select-action"
                                    onClick={selectAllDays}
                                >
                                    Select All
                                </button>
                                <button
                                    type="button"
                                    className="btn-select-action"
                                    onClick={deselectAllDays}
                                >
                                    Current Only
                                </button>
                            </div>
                        </div>

                        <div className="days-checkbox-list">
                            {allSelectedDays.map((day) => (
                                <label key={day} className="day-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={applyToDays.includes(day)}
                                        onChange={() => toggleApplyToDay(day)}
                                    />
                                    <span className="day-checkbox-label">
                                        {day}
                                        {day === selectedDay && (
                                            <span className="current-day-badge">Current</span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="timeframe-modal-footer">
                    <button
                        type="button"
                        className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        onClick={onClose}
                    >
                        <span>Cancel</span>
                    </button>
                    <button
                        type="button"
                        className="btn linear-gradient yr-submitbtn btn-sm"
                        onClick={handleSave}
                    >
                        <span>
                            <i className="fa fa-check"></i> {editMode ? 'Update Time Frames' : 'Add Time Frames'}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TimeFrameModal;
